/**
 * Centralized SVG registry for all tooth components using dynamic imports
 * This provides lazy-loading and better performance
 */

import { ToothView } from '../types/tooth.types'
import React from 'react'
import { logger } from '@utils/safeLogger'

type SVGImport = () => Promise<unknown>

// Registry for base tooth SVGs
const baseToothRegistry: Record<string, SVGImport> = {
  // Buccal views
  '1-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal1.svg'),
  '2-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal2.svg'),
  '3-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal3.svg'),
  '4-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal4.svg'),
  '5-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal5.svg'),
  '6-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal6.svg'),
  '7-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal7.svg'),
  '8-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal8.svg'),
  '9-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal9.svg'),
  '10-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal10.svg'),
  '11-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal11.svg'),
  '12-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal12.svg'),
  '13-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal13.svg'),
  '14-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal14.svg'),
  '15-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal15.svg'),
  '16-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal16.svg'),
  '17-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal17.svg'),
  '18-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal18.svg'),
  '19-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal19.svg'),
  '20-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal20.svg'),
  '21-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal21.svg'),
  '22-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal22.svg'),
  '23-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal23.svg'),
  '24-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal24.svg'),
  '25-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal25.svg'),
  '26-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal26.svg'),
  '27-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal27.svg'),
  '28-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal28.svg'),
  '29-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal29.svg'),
  '30-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal30.svg'),
  '31-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal31.svg'),
  '32-buccal': () => import('@assets/images/Tooth/buccal/ToothBuccal32.svg'),

  // Palatal views (teeth 1-16)
  '1-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal1.svg'),
  '2-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal2.svg'),
  '3-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal3.svg'),
  '4-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal4.svg'),
  '5-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal5.svg'),
  '6-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal6.svg'),
  '7-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal7.svg'),
  '8-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal8.svg'),
  '9-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal9.svg'),
  '10-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal10.svg'),
  '11-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal11.svg'),
  '12-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal12.svg'),
  '13-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal13.svg'),
  '14-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal14.svg'),
  '15-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal15.svg'),
  '16-palatal': () => import('@assets/images/Tooth/palatal/ToothPalatal16.svg'),

  // Lingual views (teeth 17-32)
  '17-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual17.svg'),
  '18-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual18.svg'),
  '19-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual19.svg'),
  '20-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual20.svg'),
  '21-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual21.svg'),
  '22-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual22.svg'),
  '23-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual23.svg'),
  '24-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual24.svg'),
  '25-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual25.svg'),
  '26-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual26.svg'),
  '27-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual27.svg'),
  '28-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual28.svg'),
  '29-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual29.svg'),
  '30-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual30.svg'),
  '31-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual31.svg'),
  '32-lingual': () => import('@assets/images/Tooth/lingual/ToothLingual32.svg'),

  // Crown views
  '1-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown1.svg'),
  '2-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown2.svg'),
  '3-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown3.svg'),
  '4-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown4.svg'),
  '5-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown5.svg'),
  '6-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown6.svg'),
  '7-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown7.svg'),
  '8-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown8.svg'),
  '9-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown9.svg'),
  '10-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown10.svg'),
  '11-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown11.svg'),
  '12-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown12.svg'),
  '13-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown13.svg'),
  '14-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown14.svg'),
  '15-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown15.svg'),
  '16-crown-top': () => import('@assets/images/Tooth/crown/ToothCrown16.svg'),
  '17-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown17.svg'),
  '18-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown18.svg'),
  '19-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown19.svg'),
  '20-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown20.svg'),
  '21-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown21.svg'),
  '22-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown22.svg'),
  '23-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown23.svg'),
  '24-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown24.svg'),
  '25-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown25.svg'),
  '26-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown26.svg'),
  '27-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown27.svg'),
  '28-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown28.svg'),
  '29-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown29.svg'),
  '30-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown30.svg'),
  '31-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown31.svg'),
  '32-crown-bottom': () =>
    import('@assets/images/Tooth/crown/ToothCrown32.svg'),
}

// SVG component cache to avoid reloading
const svgCache: Record<
  string,
  React.FunctionComponent<React.SVGProps<SVGSVGElement>>
> = {}

export interface ToothSVGs {
  Base: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  Missing?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  RootCanal?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  Pulpotomy?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  Bleeding?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  FurcationL1?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  FurcationL2?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  FurcationL3?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  Implant?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  Pontic?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  PonticLeftSupport?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  PonticRightSupport?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  PonticRightLeftSupport?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement>
  >
  Retainer?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  FractureCrownHorizontal?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement>
  >
  FractureCrownVertical?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  FractureRoot?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  FractureRootHorizontal?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement>
  >
  FractureMissingDistal?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  FractureMissingMesial?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  FractureMissingPalatalDistal?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement>
  >
  FractureMissingPalatalMesial?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement>
  >
}

/**
 * Helper to load an SVG from the registry
 */
async function loadSVG(
  key: string,
): Promise<React.FunctionComponent<React.SVGProps<SVGSVGElement>> | undefined> {
  // Check cache first
  if (svgCache[key]) {
    return svgCache[key]
  }

  const importFn = baseToothRegistry[key]
  if (!importFn) {
    return undefined
  }

  try {
    const module = (await importFn()) as {
      ReactComponent?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
    }
    const component = module.ReactComponent
    if (component) {
      svgCache[key] = component
      return component
    }
    return undefined
  } catch (error) {
    logger.error(`Failed to load SVG: ${key}`, error)
    return undefined
  }
}

/**
 * Get SVG components for a specific tooth and view
 * Returns synchronous components - call preloadToothSVGs first for best UX
 */
export function getToothSVGs(toothNumber: number, view: ToothView): ToothSVGs {
  const key = `${toothNumber}-${view}`
  const Base = svgCache[key]

  if (!Base) {
    // eslint-disable-next-line no-console
    console.warn(`SVG not preloaded for ${key}. Tooth will use placeholder.`)
    const Placeholder = (): null => null
    return {
      Base: Placeholder as React.FunctionComponent<
        React.SVGProps<SVGSVGElement>
      >,
    }
  }

  return {
    Base,
    // TODO: Add other SVG variants
  }
}

/**
 * Preload tooth SVGs to avoid loading delays
 * Call this when the tooth component mounts or on app init
 */
export async function preloadToothSVGs(
  toothNumber: number,
  view: ToothView,
): Promise<void> {
  const key = `${toothNumber}-${view}`
  await loadSVG(key)
}

/** True when the SVG for this tooth/view is already in the in-memory cache. */
export function isToothSVGPreloaded(
  toothNumber: number,
  view: ToothView,
): boolean {
  return Boolean(svgCache[`${toothNumber}-${view}`])
}

/**
 * Preload all base tooth SVGs for a given view type
 * Useful for preloading all buccal views at once
 */
export async function preloadAllToothSVGsForView(
  view: ToothView,
): Promise<void> {
  const promises: Promise<void>[] = []

  for (let i = 1; i <= 32; i++) {
    promises.push(preloadToothSVGs(i, view))
  }

  await Promise.all(promises)
}
